import { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import { ApiError } from "@/api/client";
import {
  useCreateSupplier,
  useUpdateSupplier,
  type Supplier,
} from "@/api/suppliers";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface Props {
  /** Open in create mode (true) or closed (false) — used when supplier is null. */
  open: boolean;
  /** When set, the dialog is in edit mode for this supplier. */
  supplier: Supplier | null;
  onClose: () => void;
}

export function SupplierDialog({ open, supplier, onClose }: Props) {
  const { t } = useTranslation();
  const isEdit = supplier != null;
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [serverError, setServerError] = useState<string | null>(null);
  const create = useCreateSupplier();
  const update = useUpdateSupplier();

  useEffect(() => {
    setCode(supplier?.code ?? "");
    setName(supplier?.name ?? "");
    setServerError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-seed only when a different row opens; a refetched supplier must not wipe in-flight edits
  }, [supplier?.id, open]);

  const isOpen = open || isEdit;
  const isValid = code.trim() !== "" && name.trim() !== "";
  const isPending = create.isPending || update.isPending;

  const close = () => {
    setCode("");
    setName("");
    setServerError(null);
    onClose();
  };

  const onError = (err: unknown) => {
    setServerError(
      (err instanceof ApiError && err.error?.message) ||
        t("suppliers.saveError"),
    );
  };

  const handleSave = () => {
    setServerError(null);
    if (isEdit) {
      update.mutate(
        { id: supplier!.id, code: code.trim(), name: name.trim() },
        { onSuccess: close, onError },
      );
    } else {
      create.mutate(
        { code: code.trim(), name: name.trim() },
        { onSuccess: close, onError },
      );
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(v) => !v && close()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isEdit ? t("suppliers.editTitle") : t("suppliers.newTitle")}
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="supplier-code">{t("suppliers.codeLabel")}</Label>
            <Input
              id="supplier-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              autoFocus
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="supplier-name">{t("suppliers.nameLabel")}</Label>
            <Input
              id="supplier-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          {serverError && (
            <p className="text-destructive text-sm">{serverError}</p>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={close}>
            {t("common.buttons.cancel")}
          </Button>
          <Button onClick={handleSave} disabled={!isValid || isPending}>
            {isEdit ? t("common.buttons.save") : t("common.buttons.create")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
